import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { catchError, Observable, of } from "rxjs";
import { Opportunity } from "../models/opportunity.model";
import { environment } from "../../environments/environment";
import { ClaimItReferral } from "../models/claim-it-referral.model";
import { Page } from "../models/page.model";
import { OpportunitySource } from "../models/opportunity-source.model";

@Injectable({
  providedIn: "root"
})
export class OpportunityService {
  constructor(private http: HttpClient) {}

  public getRecentOpportunities(): Observable<Opportunity[]> {
    return this.http
      .get<
        Opportunity[]
      >(`${environment.fulcrumUrl}/api/v1/opportunity/recent-referral`, this.buildBasicHttpOptions())
      .pipe(
        catchError((error: any): Observable<Opportunity[]> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of(new Array<Opportunity>());
          }
          throw error;
        })
      );
  }

  /**
   * Retrieves the list of opportunity sources
   */
  public getOpportunitySources(): Observable<OpportunitySource[]> {
    return this.http
      .get<
        OpportunitySource[]
      >(`${environment.fulcrumUrl}/api/v3/opportunity/sources`, this.buildBasicHttpOptions())
      .pipe(
        catchError((error: any): Observable<OpportunitySource[]> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of(new Array<OpportunitySource>());
          }
          throw error;
        })
      );
  }

  public getAvailableReferrals(
    sortField: string | null,
    sortOrder: string | null,
    pageNumber: number,
    pageSize: number
  ): Observable<ClaimItReferral[]> {
    return this.http
      .get<
        ClaimItReferral[]
      >(`${environment.fulcrumUrl}/api/v1/opportunity/available-referral`, this.buildHttpOptions(sortField, sortOrder, pageNumber, pageSize, {}, null))
      .pipe(
        catchError((error: any): Observable<ClaimItReferral[]> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of(new Array<ClaimItReferral>());
          } else if (error.status === 401) {
            console.log("Not Authorized");
            return of(new Array<ClaimItReferral>());
          }

          throw error;
        })
      );
  }

  public getPurchasedReferrals(
    sortField: string | null,
    sortOrder: string | null,
    pageNumber: number,
    pageSize: number
  ): Observable<ClaimItReferral[]> {
    return this.http
      .get<
        ClaimItReferral[]
      >(`${environment.fulcrumUrl}/api/v1/opportunity/purchased-referral`, this.buildHttpOptions(sortField, sortOrder, pageNumber, pageSize, {}, null))
      .pipe(
        catchError((error: any): Observable<ClaimItReferral[]> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of(new Array<ClaimItReferral>());
          } else if (error.status === 401) {
            console.log("Not Authorized");
            return of(new Array<ClaimItReferral>());
          }

          throw error;
        })
      );
  }

  public getReferral(opportunityId: number): Observable<ClaimItReferral> {
    return this.http
      .get<ClaimItReferral>(`${environment.fulcrumUrl}/api/v1/opportunity/${opportunityId}`)
      .pipe(
        catchError((error: any): Observable<ClaimItReferral> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of();
          } else if (error.status === 401) {
            console.log("Not Authorized");
            return of();
          }

          throw error;
        })
      );
  }

  public getAllReferrals(
    sortField: string | null,
    sortOrder: string | null,
    pageNumber: number,
    pageSize: number,
    filters: { [key: string]: string } = {},
    marketplaceId: number,
    username: string | null
  ): Observable<Page<ClaimItReferral>> {
    return this.http
      .get<
        Page<ClaimItReferral>
      >(`${environment.fulcrumUrl}/api/v1/marketplace-opportunities/${marketplaceId}`, this.buildHttpOptions(sortField, sortOrder, pageNumber, pageSize, filters, username))
      .pipe(
        catchError((error: any): Observable<Page<ClaimItReferral>> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of();
          } else if (error.status === 401) {
            console.log("Not Authorized");
            return of();
          }

          throw error;
        })
      );
  }

  public getAllOpportunities(
    sortField: string | null,
    sortOrder: string | null,
    pageNumber: number,
    pageSize: number,
    filters: { [key: string]: string } = {},
    username: string | null,
    showExpiration: boolean
  ): Observable<Page<ClaimItReferral>> {
    var url = showExpiration
      ? `${environment.fulcrumUrl}/api/v1/marketplace-opportunities-by-expiration`
      : `${environment.fulcrumUrl}/api/v1/marketplace-opportunities`;

    return this.http
      .get<
        Page<ClaimItReferral>
      >(url, this.buildHttpOptions(sortField, sortOrder, pageNumber, pageSize, filters, username))
      .pipe(
        catchError((error: any): Observable<Page<ClaimItReferral>> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of();
          } else if (error.status === 401) {
            console.log("Not Authorized");
            return of();
          }

          throw error;
        })
      );
  }

  public getAcquiredReferrals(
    sortField: string | null,
    sortOrder: string | null,
    pageNumber: number,
    pageSize: number,
    username: string
  ): Observable<Page<ClaimItReferral>> {
    return this.http
      .post<
        Page<ClaimItReferral>
      >(`${environment.fulcrumUrl}/api/v1/acquired-marketplace-opportunities`, { username: username }, this.buildHttpOptions(sortField, sortOrder, pageNumber, pageSize, {}, null))
      .pipe(
        catchError((error): Observable<Page<ClaimItReferral>> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of();
          } else if (error.status === 401) {
            console.log("Not Authorized");
            return of();
          }

          throw error;
        })
      );
  }

  private buildBasicHttpOptions() {
    return this.buildHttpOptions(null, null, 0, 0, {}, null);
  }

  private buildHttpOptions(
    sortField: string | null,
    sortOrder: string | null,
    pageNumber: number,
    pageSize: number,
    filters: { [key: string]: string },
    username: string | null
  ) {
    let httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization:
          "Basic " + btoa(`${environment.claimItApiUsername}:${environment.claimItApiPassword}`)
      }),
      params: new HttpParams().set("page", pageNumber.toString()).set("size", pageSize.toString())
    };

    // Handle sorting
    if (sortField && sortOrder) {
      const entitySortField: string = this.getSortField(sortField);
      httpOptions.params = httpOptions.params.set("sort", `${entitySortField},${sortOrder}`);
    } else {
      httpOptions.params = httpOptions.params.set("size", 20);
    }

    // Handle username
    if (username != null) {
      httpOptions.params = httpOptions.params.set("username", username);
    }

    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value != null && value !== "") {
        httpOptions.params = httpOptions.params.set(key, value);
      }
    });

    return httpOptions;
  }

  private getSortField(sortField: string): string {
    if (sortField === "addDate") {
      return "addDate";
    } else if (sortField === "status") {
      return "marketplaceStatus";
    } else if (sortField === "name") {
      return "lastName";
    } else if (sortField === "city") {
      return "city";
    } else if (sortField === "category") {
      return "businessLineCategory";
    } else if (sortField === "businessLines") {
      return "opportunityBusinessLineEntities";
    } else if (sortField === "estimatedPremium") {
      return "premium";
    } else if (sortField === "industry") {
      return "industryCode";
    } else if (sortField === "noOfEmployees") {
      return "businessSize";
    } else if (sortField === "yearsInBusiness") {
      return "businessYears";
    }
    return sortField;
  }
}
